import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Clock, AlertTriangle } from "lucide-react";

const RAZORPAY_SCRIPT_URL = "https://checkout.razorpay.com/v1/checkout.js";
import { api } from "@/lib/api";
import { extractError, useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const CURRENCY_SYMBOLS = {
  INR: "₹",
  USD: "$",
  AUD: "A$",
  EUR: "€",
  GBP: "£",
};

const STATUS_LABEL = {
  trialing: { label: "Free trial", cls: "bg-sky-100 text-sky-900 border-sky-200" },
  active: { label: "Active", cls: "bg-emerald-100 text-emerald-900 border-emerald-200" },
  expired: { label: "Expired", cls: "bg-red-100 text-red-900 border-red-200" },
  cancelled: { label: "Cancelled", cls: "bg-zinc-200 text-zinc-700 border-zinc-300" },
};

export default function Subscription() {
  const { user, refresh } = useAuth();
  const [data, setData] = useState(null);
  const [subscribing, setSubscribing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const load = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const { data } = await api.get("/subscription");
      setData(data);
    } catch (err) {
      setLoadError(extractError(err));
      setData(null);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  if (!user || user === false) {
    return <div className="text-sm text-zinc-500">Loading…</div>;
  }

  if (user.role !== "admin") {
    return (
      <div className="max-w-xl bg-white border border-black/10 rounded-md p-8 text-center" data-testid="subscription-no-access">
        <h2 className="font-serif text-2xl tracking-tight">Admin only</h2>
        <p className="text-sm text-zinc-600 mt-2">Only the workspace admin can view billing & subscription details.</p>
      </div>
    );
  }

  if (loading) return <div className="text-sm text-zinc-500">Loading…</div>;

  const summary = data?.summary || {};
  const plans = data?.plans || [];
  const invoices = data?.invoices || [];
  const company_name = data?.company_name || "Planniq";
  const company_currency = data?.company_currency || "INR";
  const company_currency_symbol = data?.company_currency_symbol || CURRENCY_SYMBOLS[company_currency] || company_currency;
  const meta = STATUS_LABEL[summary.subscription_status] || { label: summary.subscription_status, cls: "" };

  const getPlanPrice = (plan) => {
    if (plan.pricing?.length) {
      const item = plan.pricing.find((x) => x.currency === company_currency);
      if (item) return item;
      return plan.pricing[0];
    }
    return { price: plan.price || 0, currency: plan.currency || company_currency };
  };

  const formatMoney = (price, currency, symbol) => {
    const currencySymbol = symbol || CURRENCY_SYMBOLS[currency] || currency;
    return `${currencySymbol} ${Number(price || 0).toLocaleString()}`;
  };

  const subscribe = async (planId) => {
    setSubscribing(planId);
    try {
      const { data } = await api.post("/subscription/checkout", { plan_id: planId });
      if (!data?.checkout?.order_id) {
        throw new Error("Razorpay checkout could not be initialized");
      }
      const script = document.createElement("script");
      script.src = RAZORPAY_SCRIPT_URL;
      script.async = true;
      script.onload = () => {
        const options = {
          ...data.checkout,
          key: data.checkout.key_id,
          handler: async (response) => {
            try {
              await api.post("/subscription/verify", {
                plan_id: planId,
                order_id: response.razorpay_order_id,
                payment_id: response.razorpay_payment_id,
                signature: response.razorpay_signature,
              });
              toast.success("Subscription activated");
              await refresh();
              load();
            } catch (err) {
              toast.error(extractError(err));
            }
          },
        };
        const rzp = new window.Razorpay(options);
        rzp.open();
      };
      script.onerror = () => {
        toast.error("Razorpay script failed to load");
      };
      document.body.appendChild(script);
    } catch (err) { toast.error(extractError(err)); }
    finally { setSubscribing(null); }
  };

  const trialDaysLeft = (() => {
    if (summary.subscription_status !== "trialing" || !summary.trial_ends_at) return null;
    const ms = new Date(summary.trial_ends_at) - new Date();
    return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
  })();

  return (
    <div className="space-y-6 max-w-5xl" data-testid="subscription-page">
      <div>
        <div className="text-xs uppercase tracking-[0.25em] text-zinc-500">Billing</div>
        <h1 className="font-serif text-4xl tracking-tighter mt-1 text-[#1A1A1A]">Subscription</h1>
      </div>

      <Card className="bg-white border-black/10">
        <CardHeader><CardTitle className="text-sm uppercase tracking-[0.2em] text-zinc-500 font-medium">Current plan</CardTitle></CardHeader>
        <CardContent>
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <div className="font-serif text-2xl tracking-tighter">{summary.plan_name || "—"}</div>
              <div className="text-sm text-zinc-600 mt-1">{company_name}</div>
              <div className="mt-3"><Badge variant="outline" className={meta.cls} data-testid="subscription-status-badge">{meta.label}</Badge></div>
            </div>
            <div className="text-sm text-zinc-700 text-right">
              {summary.subscription_status === "trialing" && (
                <div className="inline-flex items-center gap-2 px-3 py-2 rounded bg-sky-50 border border-sky-200">
                  <Clock size={14} className="text-sky-700" />
                  <span data-testid="trial-days-left">{trialDaysLeft} days left in trial</span>
                </div>
              )}
              {summary.subscription_status === "active" && summary.current_period_end && (
                <div>Renews on <span className="font-medium">{new Date(summary.current_period_end).toLocaleDateString()}</span></div>
              )}
              {summary.subscription_status === "expired" && (
                <div className="inline-flex items-center gap-2 px-3 py-2 rounded bg-red-50 border border-red-200 text-red-800">
                  <AlertTriangle size={14} />
                  <span>Your access has expired — pick a plan below to continue.</span>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white border-black/10">
        <CardHeader><CardTitle className="text-sm uppercase tracking-[0.2em] text-zinc-500 font-medium">Available plans</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {plans.map((p) => (
              <div key={p.id} className="border border-black/10 rounded-md p-5 flex flex-col" data-testid={`subscription-plan-${p.code}`}>
                <div className="font-serif text-xl tracking-tight">{p.name}</div>
                <div className="mt-2 flex items-baseline gap-1">
                  {(() => {
                    const planPrice = getPlanPrice(p);
                    const symbol = CURRENCY_SYMBOLS[planPrice.currency] || planPrice.currency;
                    return <span className="font-serif text-3xl tracking-tighter">{formatMoney(planPrice.price, planPrice.currency, company_currency)}</span>;
                  })()}
                  <span className="text-xs text-zinc-500">/ {p.duration_days} days</span>
                </div>
                {p.description && <div className="text-sm text-zinc-600 mt-2">{p.description}</div>}
                <ul className="text-sm text-zinc-700 mt-4 space-y-1 flex-1">
                  <li className="flex items-center gap-2"><Check size={14} className="text-emerald-700" /> Unlimited bookings</li>
                  <li className="flex items-center gap-2"><Check size={14} className="text-emerald-700" /> Unlimited users</li>
                  <li className="flex items-center gap-2"><Check size={14} className="text-emerald-700" /> Priority support</li>
                </ul>
                <Button
                  onClick={() => subscribe(p.id)}
                  disabled={subscribing === p.id}
                  className="mt-5 w-full bg-[#D85C38] hover:bg-[#B84A2A] text-white rounded-full"
                  data-testid={`subscribe-btn-${p.code}`}
                >
                  {summary.plan_id === p.id
                    ? (summary.subscription_status === "active" ? "Renew" : "Choose plan")
                    : subscribing === p.id ? "Redirecting…" : "Upgrade"}
                </Button>
              </div>
            ))}
          </div>
          <div className="mt-4 text-xs text-zinc-500">Checkout opens Razorpay securely for real payment processing when the gateway is enabled.</div>
     
        </CardContent>
      </Card>

      <Card className="bg-white border-black/10">
        <CardHeader><CardTitle className="text-sm uppercase tracking-[0.2em] text-zinc-500 font-medium">Billing history</CardTitle></CardHeader>
        <CardContent className="p-0">
          {invoices.length === 0 ? (
            <div className="p-8 text-center text-sm text-zinc-500">No subscription invoices yet.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow><TableHead>Invoice</TableHead><TableHead>Plan</TableHead><TableHead>Period</TableHead><TableHead className="text-right">Amount</TableHead><TableHead>Status</TableHead></TableRow>
              </TableHeader>
              <TableBody>
                {invoices.map((inv) => (
                  <TableRow key={inv.id} data-testid={`sub-invoice-row-${inv.id}`}>
                    <TableCell className="font-medium">{inv.invoice_number}</TableCell>
                    <TableCell>{inv.plan_name}</TableCell>
                    <TableCell className="text-zinc-600 text-xs">{(inv.period_start || "").slice(0, 10)} → {(inv.period_end || "").slice(0, 10)}</TableCell>
                    <TableCell className="text-right">{formatMoney(inv.amount, inv.currency || company_currency, company_currency_symbol)}</TableCell>
                    <TableCell><Badge variant="outline" className="bg-emerald-100 text-emerald-900 border-emerald-200 capitalize">{inv.status}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
